// src/redux/slices/allRoleSlice.js
import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import {
  getAllRole,
  createRole,
  deleteRole,
  updateRole,
} from "@/api/new-api/role/role";

export const fetchAllRoles = createAsyncThunk(
  "roles/fetchAll",
  async (_, { rejectWithValue }) => {
    try {
      const response = await getAllRole();
      if (response.status === "success") {
        return response.data.roles || [];
      } else {
        return rejectWithValue(response.message || "Failed to fetch roles.");
      }
    } catch (error) {
      return rejectWithValue(error?.message || "Something went wrong.");
    }
  }
);

export const addRole = createAsyncThunk(
  "roles/createRole",
  async (data, { rejectWithValue }) => {
    try {
      const response = await createRole(data);
      if (response.status === "success") {
        return response.data.role;
      } else {
        return rejectWithValue(response.message || "Failed to add role.");
      }
    } catch (error) {
      return rejectWithValue(error?.message || "Something went wrong.");
    }
  }
);

// how to use
// dispatch(editRole({ id: roleId, data: updatedRoleData }));

export const editRole = createAsyncThunk(
  "roles/updateRole",
  async ({ id, data }, { rejectWithValue }) => {
    try {
      const response = await updateRole(id, data);
      if (response.status === "success") {
        return response.data.role;
      } else {
        return rejectWithValue(response.message || "Failed to update role.");
      }
    } catch (error) {
      return rejectWithValue(error?.message || "Something went wrong.");
    }
  }
);

export const removeRole = createAsyncThunk(
  "roles/delete",
  async (id, { rejectWithValue }) => {
    try {
      const response = await deleteRole(id);
      if (response.status === "success") {
        return id;
      } else {
        return rejectWithValue(response.message || "Failed to delete role.");
      }
    } catch (error) {
      return rejectWithValue(error?.message || "Something went wrong.");
    }
  }
);

const allRoleSlice = createSlice({
  name: "roles",
  initialState: {
    roles: [],
    loading: false,
    error: null,
  },
  reducers: {
    resetRoles: (state) => {
      state.roles = [];
      state.loading = false;
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchAllRoles.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchAllRoles.fulfilled, (state, action) => {
        state.loading = false;
        state.roles = action.payload;
      })
      .addCase(fetchAllRoles.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(addRole.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(addRole.fulfilled, (state, action) => {
        state.loading = false;
        state.roles.push(action.payload);
      })
      .addCase(addRole.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(removeRole.fulfilled, (state, action) => {
        state.roles = state.roles.filter((role) => role.id !== action.payload);
      })
      .addCase(removeRole.rejected, (state, action) => {
        state.error = action.payload;
      })
      .addCase(editRole.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(editRole.fulfilled, (state, action) => {
        state.loading = false;
        const updatedRole = action.payload;
        state.roles = state.roles.map((role) =>
          role.id === updatedRole.id ? updatedRole : role
        );
      })
      .addCase(editRole.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      });
  },
});

export const { resetRoles } = allRoleSlice.actions;

export default allRoleSlice.reducer;
